import { useSearch } from "@tanstack/react-router";
import { Tag } from "antd";
import { useGetLabelsQuery } from "api/TenantSlug/Label/hooks.ts";
import { AddLabelDrawer } from "components/Drawers/Labels/AddLabelDrawer.tsx";
import { EditLabelDrawer } from "components/Drawers/Labels/EditLabelDrawer.tsx";
import { Table } from "components/UI/Table";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations.ts";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { CountryCell } from "components/UI/Table/UI/CountryCell/CountryCell.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { Fragment } from "react";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums.ts";
import { DrawerId, TableId } from "types/models.ts";

import LabelData = App.Data.LabelData;
import GetLabelsResponse = App.Http.Responses.Label.GetLabelsResponse;

interface SearchParams {
    [`editLabel-labelId`]: string;
}

export const LabelsTable = () => {
    const { t } = usePaldockTranslation("tables.labels");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const tableId = TableId.LabelsTable;
    const tableAtomInstance = tableAtom<LabelData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));

    const searchParams = useSearch({ strict: false }) as SearchParams;

    const { hasPermission } = usePermission();

    const getLabelsQuery = useGetLabelsQuery(
        {
            filter: {
                filter: {
                    ...(tableState.filter?.filter || {}),
                },
                include: "country",
                sort: tableState.filter?.sort ?? "name",
            },
            page: {
                number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
                size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
            },
        },
        { enabled: false }
    );

    const { tableData, fetchQuery, refetchData } = useTableDataApiOperations<GetLabelsResponse>(
        tableId,
        getLabelsQuery
    );
    const { isLoading, isFetching } = fetchQuery;

    const columnConfig: TableColumnType<LabelData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "name",
            key: "name",
            inputType: "text",
            filterable: true,
            filterType: FILTER_TYPES.string,
            sorter: true,
            defaultSorter: true,
            ellipsis: true,
            width: 150,
            render: (_, record) => (
                <Tag color={record.color ?? undefined} className="!m-0">
                    {record.name}
                </Tag>
            ),
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("type")} />,
            dataIndex: "type",
            key: "type",
            inputType: "text",
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterOptions: [
                { label: t("labels.label"), value: "label" },
                { label: t("labels.category"), value: "category" },
            ],
            render: (type) => (type === "label" ? t("labels.label") : t("labels.category")),
            sorter: true,
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("country")} />,
            dataIndex: "country",
            key: "country",
            inputType: "text",
            render: (_, { country }) => (country ? <CountryCell country={country} /> : "-"),
            filterable: true,
            filterType: FILTER_TYPES.countrySelect,
            filterName: "country.name",
            sorter: true,
            ellipsis: true,
            width: 120,
        },
    ];

    const { getRowHandlers, getRowClassName } = useRowHandlers<LabelData>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.EditLabel,
                drawerParam: `${DrawerId.EditLabel}-labelId`,
                permission: PermissionEnum.LabelsUpdate,
            },
        ],
    });

    return (
        <Fragment>
            {hasPermission(PermissionEnum.LabelsCreate) && <AddLabelDrawer onSuccess={() => refetchData()} />}
            {hasPermission(PermissionEnum.LabelsUpdate) && searchParams[`${DrawerId.EditLabel}-labelId`] && (
                <EditLabelDrawer onSuccess={() => refetchData()} />
            )}
            <Table<GetLabelsResponse, LabelData>
                tableId={tableId}
                columnConfig={columnConfig}
                loading={isLoading || isFetching}
                dataSource={tableData?.labels || []}
                data={tableData as GetLabelsResponse}
                rowKey={(record) => record.id}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                addEntityData={{
                    drawerId: DrawerId.AddLabel,
                    permission: PermissionEnum.LabelsCreate,
                }}
                autoScrollWidth={true}
                enableFilters={true}
            />
        </Fragment>
    );
};
